// Catalog.ID Client — Account Unlock Sheet Payload (format v1)
// ============================================================================
// The validator seals an envelope (password-reset plan §3.3):
//
//   magic 'CIDP' (4) | version (1) | rsk_key_id u16 BE (2) | nonce (12) |
//   AES-256-GCM ciphertext+tag over { cid, email, phone, epoch, issued_at }
//
// The client can only read the FRAMING — the contents are sealed under the
// validator-only RSK: opaque to the member, the core server, and any thief.
//
// Amendment 2026-09-03 — outer 'CIDA' framing. The account id inside the seal
// is unreadable to the client, so the reset flow used to make the member TYPE
// it before scanning; a typo landed on the server's anti-enumeration decoy
// ("if this sheet is valid, a link was sent") and the member waited for mail
// that never came. The id is printed in plain text in the header of the same
// sheet, so carrying it in the QR leaks nothing. The QR payload is now:
//
//   magic 'CIDA' (4) | version (1) | cid_len u8 (1) | cid (ASCII, lowercase) |
//   sealed envelope (the CIDP bytes above, verbatim, to the end)
//
// This framing is CLIENT-ONLY: the validator and the core server never see it
// — reset/begin still submits the INNER envelope, and resetCardChecksum still
// hashes the inner envelope, so support's cross-check against the server's
// bookkeeping fingerprint keeps working. Sheets printed before the amendment
// carry a bare 'CIDP' payload and still decode, with a null cid; the flow then
// falls back to asking for the id.
//
// decodeResetCard validates the framing so a scan can tell an unlock sheet apart
// from a Recovery Sheet ('CIRC') or a random QR; authenticity and
// confidentiality live entirely in the validator's GCM seal. Unlike the
// Recovery Sheet there is no client-side MAC: the seal already authenticates
// every byte, and the checksum below is only a print/scan sanity affordance.

import { sha256 } from '@noble/hashes/sha2.js';

export const RESET_CARD_MAGIC = 'CIDP';
export const RESET_CARD_VERSION = 1;
/** Outer, client-only framing that carries the plain-text account id. */
export const ACCESS_CARD_MAGIC = 'CIDA';
export const ACCESS_CARD_VERSION = 1;

const KEY_ID_LEN = 2;
const NONCE_LEN = 12;
const TAG_LEN = 16;
const HEADER_LEN = 4 + 1 + KEY_ID_LEN + NONCE_LEN;
/** magic (4) | version (1) | cid_len (1) */
const OUTER_HEADER_LEN = 4 + 1 + 1;

/** A Catalog.ID: exactly 10 lowercase letters/digits (server CidService). */
const CID_PATTERN = /^[a-z0-9]{10}$/;

export interface ResetCardPayload {
    version: typeof RESET_CARD_VERSION;
    /** Which validator RSK sealed the token (framing metadata, not secret). */
    rskKeyId: number;
    /** The account id read off the sheet, or null for a sheet printed before the
     *  CIDA framing — those force the member to supply it. */
    cid: string | null;
    /** The sealed envelope exactly as the validator issued it — what
     *  reset/begin submits, and what resetCardChecksum hashes. */
    sealedToken: Uint8Array;
}

/**
 * Wrap a validator-sealed envelope with the plain-text account id for printing.
 * The sealed bytes are copied verbatim; nothing about them is inspected (a page
 * builder must be able to render whatever the server handed it).
 */
export function encodeAccessCard(input: { cid: string; sealedToken: Uint8Array }): Uint8Array {
    const cid = input.cid.trim().toLowerCase();
    if (!CID_PATTERN.test(cid)) {
        throw new Error(`"${input.cid}" is not a Catalog.ID (10 letters and digits)`);
    }
    const cidBytes = new TextEncoder().encode(cid);
    const out = new Uint8Array(OUTER_HEADER_LEN + cidBytes.length + input.sealedToken.length);
    out.set(new TextEncoder().encode(ACCESS_CARD_MAGIC), 0);
    out[4] = ACCESS_CARD_VERSION;
    out[5] = cidBytes.length;
    out.set(cidBytes, OUTER_HEADER_LEN);
    out.set(input.sealedToken, OUTER_HEADER_LEN + cidBytes.length);
    return out;
}

/**
 * Validate the framing of a scanned unlock sheet and return the sealed token
 * plus, for sheets printed with the CIDA framing, the account id.
 * Throws with a specific message on anything malformed — wrong magic/version,
 * truncation, an id that is not a Catalog.ID. Only the validator can read (or
 * vouch for) the contents.
 */
export function decodeResetCard(bytes: Uint8Array): ResetCardPayload {
    if (startsWith(bytes, ACCESS_CARD_MAGIC)) {
        return decodeOuter(bytes);
    }
    return { ...decodeSealed(bytes), cid: null };
}

function decodeOuter(bytes: Uint8Array): ResetCardPayload {
    if (bytes.length < OUTER_HEADER_LEN) {
        throw new Error('Not a Catalog.ID Account Unlock Sheet (payload too short)');
    }
    const version = bytes[4];
    if (version !== ACCESS_CARD_VERSION) {
        throw new Error(`Unsupported Account Unlock Sheet version ${version}`);
    }
    const cidLen = bytes[5];
    if (bytes.length < OUTER_HEADER_LEN + cidLen) {
        throw new Error('Not a Catalog.ID Account Unlock Sheet (payload too short)');
    }
    const cid = new TextDecoder().decode(bytes.subarray(OUTER_HEADER_LEN, OUTER_HEADER_LEN + cidLen));
    if (!CID_PATTERN.test(cid)) {
        throw new Error('Not a Catalog.ID Account Unlock Sheet (bad account id)');
    }
    // Copy the inner envelope out: it is passed to base64/hash helpers and
    // stored, and a view over a larger buffer invites byteOffset bugs there.
    const sealedToken = bytes.slice(OUTER_HEADER_LEN + cidLen);
    return { ...decodeSealed(sealedToken), cid };
}

/** Validate the validator-sealed CIDP envelope itself. */
function decodeSealed(bytes: Uint8Array): Omit<ResetCardPayload, 'cid'> {
    if (bytes.length < HEADER_LEN + TAG_LEN) {
        throw new Error('Not a Catalog.ID Account Unlock Sheet (payload too short)');
    }
    if (!startsWith(bytes, RESET_CARD_MAGIC)) {
        throw new Error('Not a Catalog.ID Account Unlock Sheet (bad magic)');
    }
    const version = bytes[4];
    if (version !== RESET_CARD_VERSION) {
        throw new Error(`Unsupported Account Unlock Sheet version ${version}`);
    }
    const rskKeyId = (bytes[5] << 8) | bytes[6];
    return { version: RESET_CARD_VERSION, rskKeyId, sealedToken: bytes };
}

function startsWith(bytes: Uint8Array, magic: string): boolean {
    return bytes.length >= 4 && new TextDecoder().decode(bytes.subarray(0, 4)) === magic;
}

/**
 * Human-readable checksum printed under the QR: first 4 bytes of the token's
 * SHA-256, hex-grouped — matches the server-side bookkeeping fingerprint
 * prefix, so support can cross-check a physical sheet against the record.
 * Always over the INNER sealed envelope, never the CIDA wrapper.
 */
export function resetCardChecksum(sealedToken: Uint8Array): string {
    const digest = sha256(sealedToken);
    return Array.from(digest.subarray(0, 4))
        .map(b => b.toString(16).padStart(2, '0').toUpperCase())
        .join('-');
}
